import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import router from '@/router';

declare global {
    interface Window {
        BrazePlugin?: any;
    }
}

// Sets up push notification event listeners. Call from onMounted.
// Does NOT request permission — call requestPushPermissionIfNeeded() post-login instead.
export async function initializePushListeners(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    PushNotifications.addListener('registration', ({ value: token }) => {
        window.BrazePlugin?.setRegisteredPushToken(token);
        window.BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
    });

    PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration error:', err.error);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification.data;
        const url: string | undefined = data?.url ?? data?.deep_link;
        if (!url) return;

        try {
            const parsed = new URL(url);
            router.push(parsed.pathname + parsed.search + parsed.hash);
        } catch {
            router.push(url);
        }
    });

    // Returning user: permission already granted — set opted_in proactively before the
    // registration event fires, then register to refresh the FCM/APNs token.
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'granted') {
        window.BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
        await PushNotifications.register();
    }
}

// Requests push permission. Call after login, not on app mount.
// Uses PushNotifications.requestPermissions() on both platforms so we get an awaitable
// result and can register immediately — BrazePlugin.requestPushPermission() on Android
// is fire-and-forget, which means the registration event never fires if the Android 13+
// permission dialog is shown as an overlay (no appStateChange fired).
export async function requestPushPermissionIfNeeded(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const permStatus = await PushNotifications.checkPermissions();
    // 'prompt-with-rationale' is Android-specific: user denied once but can be asked again.
    if (permStatus.receive !== 'prompt' && permStatus.receive !== 'prompt-with-rationale') return;

    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
        window.BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
        await PushNotifications.register();
    }
}
